import { ReactElement, useState, useEffect } from 'react';
import { Location } from 'pages/locations';
import { formatPhoneNumber } from 'lib/util';
import { IPageLocationFields } from 'types/contentful';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import makeStyles from '@material-ui/core/styles/makeStyles';

const DIRECTIONS_URL = 'https://www.google.com/maps/dir/?api=1&destination=';

type LocationDetailsCardProps = {
  location: Location;
  className?: string;
  size?: string;
  showDirections?: boolean;
  showWarningIcon?: boolean;
  showLocationIcon?: boolean;
};

const buildDirectionsUrl = (loc: IPageLocationFields): string =>
  encodeURI(`${DIRECTIONS_URL}${loc.address1} ${loc.city},${loc.state} ${loc.zipCode}`);

export default function NewLocationDetailsCard({
  location,
  className = '',
  size = 'lg',
  showDirections = true,
  showWarningIcon = false,
  showLocationIcon = false,
}: LocationDetailsCardProps): ReactElement {
  const [directionsUrl, setDirectionsUrl] = useState<string>('');

  const useStyles = makeStyles({
    tooltip: {
      fontSize: '14px',
    },
  });

  useEffect(() => {
    if (location) {
      const url = buildDirectionsUrl(location);
      setDirectionsUrl(url);
    }
  }, [location]);

  const classes = useStyles();

  return (
    <div className={`flex flex-col justify-between w-full border-transparent ${className}`}>
      <div>
        <h3 className="mb-4 flex items-center text-lg leading-none text-primary-actual">
          {showLocationIcon ? (
            <span className="mr-3 mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary">
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                className="h-5 w-5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#location-card-pin)">
                  <path
                    d="M17.5 8.33301C17.5 14.1663 10 19.1663 10 19.1663C10 19.1663 2.5 14.1663 2.5 8.33301C2.5 6.34388 3.29018 4.43623 4.6967 3.02971C6.10322 1.62318 8.01088 0.833008 10 0.833008C11.9891 0.833008 13.8968 1.62318 15.3033 3.02971C16.7098 4.43623 17.5 6.34388 17.5 8.33301Z"
                    stroke="white"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 10.833C11.3807 10.833 12.5 9.71372 12.5 8.33301C12.5 6.9523 11.3807 5.83301 10 5.83301C8.61929 5.83301 7.5 6.9523 7.5 8.33301C7.5 9.71372 8.61929 10.833 10 10.833Z"
                    stroke="white"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="location-card-pin">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </span>
          ) : null}
          {showWarningIcon ? (
            <Tooltip classes={{ tooltip: classes.tooltip }} title={''} placement="top-start">
              <WarningIcon className="text-red" style={{ fontSize: 20, marginRight: '6px' }} />
            </Tooltip>
          ) : null}
          <span className="text-lg md:text-xl font-light">{location.name}</span>
        </h3>
      </div>
      <span
        className={`block leading-relaxed text-primary-actual ${
          size == 'lg' ? 'text-base' : 'text-sm'
        }`}
      >
        {location?.address1 + ' '}
        {location?.address2 && <>{location.address2 + ' '}</>}
        {location?.address3 && <>{location.address3 + ' '}</>}
      </span>
      <span
        className={`block leading-relaxed text-primary-actual ${
          size == 'lg' ? 'text-base' : 'text-sm'
        }`}
      >
        {location?.city + ', ' + location?.state + ' ' + location?.zipCode}
      </span>
      {showDirections ? (
        <span className={`mt-1 block ${size == 'lg' ? 'text-base' : 'text-sm'}`}>
          <a
            href={directionsUrl || undefined}
            tabIndex={-1}
            target={directionsUrl ? '_blank' : undefined}
            rel="noopener noreferrer"
          >
            <span className="text-tertiary-actual underline focus:outline-none focus-visible:outline-none">
              Directions
            </span>
            {location?.currentDistance ? ` (${location.currentDistance.toPrecision(3)} miles)` : ''}
          </a>
        </span>
      ) : null}
      {location?.phoneNumber ? (
        <div className="mt-1">
          <a
            tabIndex={-1}
            className={`inline-block teal-fontcolor underline ${
              size == 'lg' ? 'text-base' : 'text-sm'
            } underline focus:outline-none focus-visible:outline-none`}
            href={`tel:${location?.phoneNumber}`}
          >
            {formatPhoneNumber(location.phoneNumber)}
          </a>
        </div>
      ) : null}
    </div>
  );
}
